import { cn } from '@repo/tailwind-config/tailwind.functions'
import ActionButton from '@repo/ui/action-button'
import { DragHandleProps } from '@repo/ui/draggable-list'
import { DeleteIcon, DragIcon } from '@repo/ui/icons'
import Input from '@repo/ui/input'
import RadioInput from '@repo/ui/radio-input'
import Typography from '@repo/ui/typography'
import { useState } from 'react'

import { numberToLetter } from '../../../utils/functions/transform-to-char'

interface LanguageTestAnswerCreationItemProps {
  index?: number
  isDragging?: boolean
  dragHandleProps?: DragHandleProps
  isLoading?: boolean
  initialLabel?: string
  showRadioButton?: boolean
  isCorrect?: boolean
  hasError?: boolean
  errorMessage?: string
  onUpdate?: (label: string, isCorrect: boolean) => void
  onDelete?: () => void
  onCorrectChange?: (isCorrect: boolean) => void
}

const LanguageTestAnswerCreationItem = ({
  isDragging,
  dragHandleProps,
  isLoading = false,
  initialLabel = '',
  showRadioButton = true,
  isCorrect = false,
  index = 0,
  hasError = false,
  errorMessage,
  onCorrectChange,
  onUpdate,
  onDelete
}: LanguageTestAnswerCreationItemProps) => {
  const [answerLabel, setAnswerLabel] = useState(initialLabel)

  const handleCorrectChange = () => {
    onCorrectChange?.(true)
    onUpdate?.(answerLabel, true)
  }

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setAnswerLabel(newValue)
    onUpdate?.(newValue, isCorrect)
  }

  return (
    <div>
      <div
        className={cn(
          'flex items-center py-3 px-4 bg-white-hover border border-grey-light rounded-lg shadow-sm transition-shadow',
          isDragging ? 'shadow-lg opacity-75' : '',
          isCorrect && 'border-green-light bg-green-light',
          hasError && 'border-red'
        )}
      >
        {/* Drag Handle */}
        <div
          {...dragHandleProps?.attributes}
          {...dragHandleProps?.listeners}
          style={dragHandleProps?.style}
          className="flex items-center justify-center w-6 h-6 text-gray-300 cursor-grab"
        >
          <DragIcon className="scale-125" />
        </div>

        <div className="flex items-center justify-center w-6 h-6 text-xs font-semibold text-grey-lighter">
          {numberToLetter(index)}
        </div>

        <div className="flex-1 flex items-center gap-3">
          {showRadioButton && (
            <RadioInput
              onCheck={handleCorrectChange}
              checked={isCorrect}
              disabled={isLoading}
              variant="success"
              label="Correct"
            />
          )}
          <div className="flex-1">
            <Input
              type="text"
              autoCapitalize="off"
              value={answerLabel}
              onChange={handleLabelChange}
              disabled={isLoading}
              className={hasError ? 'border-red focus:border-red' : ''}
            />
          </div>

          <div className="flex items-center gap-2">
            {onDelete && (
              <ActionButton
                variant="danger"
                icon={DeleteIcon}
                onClick={onDelete}
                disabled={isLoading}
              />
            )}
          </div>
        </div>
      </div>
      {hasError && (
        <div className="text-red text-sm mt-1">
          <Typography variant="small">{errorMessage}</Typography>
        </div>
      )}
    </div>
  )
}

export default LanguageTestAnswerCreationItem
